import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  OnInit,
  Input,
} from '@angular/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { provideNativeDateAdapter } from '@angular/material/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { IttWrapperComponent } from '../itt-wrapper/itt-wrapper.component';
import { Router } from '@angular/router';
import { TenderingService } from '@/app/services/tendering-services/tendering.service';
import { NgToastService } from 'ng-angular-popup';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-itt-commercial-evaluation-weightage',
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [
    MatButtonModule,
    MatSelectModule,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatDatepickerModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './itt-commercial-evaluation-weightage.component.html',
  styleUrl: './itt-commercial-evaluation-weightage.component.css',
})
export class IttCommercialEvaluationWeightageComponent implements OnInit {
  @Output() closeModalEvent = new EventEmitter<boolean>();
  @Input() ITTDeveloperData: any = [];

  projectName: string;
  projectId: number;
  projectIdentifier: string;
  totalWeight:number = 0;
  closeModal = true;

  priceCompetitivenessControl = new FormControl('', Validators.required);
  paymentTermsControl = new FormControl('', Validators.required);
  financialStabilityControl = new FormControl('', Validators.required);
  commercialRiskAllocationControl = new FormControl('', Validators.required);

  constructor(
    private IttWrapperComponent: IttWrapperComponent,
    private router: Router,
    private TenderingService: TenderingService,
    private toast: NgToastService,
    private route: ActivatedRoute
  ) {}
  ngOnInit(): void {
    this.projectIdentifier = this.TenderingService.getProjectIdentifier();
    this.route.queryParams.subscribe((params) => {
      this.projectName = params['projectName'];
    });
    this.route.paramMap.subscribe((params) => {
      this.projectId = params.get('projectId')
        ? parseInt(params.get('projectId'))
        : null;
    });

    /**
     * NOTE: On edit or when we go back and forth between wizards, we will
     *  set initial value if it exists
     */
    if (
      this.ITTDeveloperData?.commercial_evaluation?.price_competitiveness &&
      this.ITTDeveloperData?.commercial_evaluation?.payment_terms &&
      this.ITTDeveloperData?.commercial_evaluation?.financial_stability &&
      this.ITTDeveloperData?.commercial_evaluation?.commercial_risk_allocation
    ) {
      this.priceCompetitivenessControl.setValue(
        this.ITTDeveloperData?.commercial_evaluation?.price_competitiveness
      );
      this.paymentTermsControl.setValue(
        this.ITTDeveloperData?.commercial_evaluation?.payment_terms
      );
      this.financialStabilityControl.setValue(
        this.ITTDeveloperData?.commercial_evaluation?.financial_stability
      );
      this.commercialRiskAllocationControl.setValue(
        this.ITTDeveloperData?.commercial_evaluation?.commercial_risk_allocation
      );
    }
    this.priceCompetitivenessControl.valueChanges.subscribe(() => this.calculateTotal());
    this.paymentTermsControl.valueChanges.subscribe(() => this.calculateTotal());
    this.financialStabilityControl.valueChanges.subscribe(() => this.calculateTotal());
    this.commercialRiskAllocationControl.valueChanges.subscribe(() => this.calculateTotal());

    this.calculateTotal();
  }

  calculateTotal(): void {
    this.totalWeight =
      Number(this.priceCompetitivenessControl.value) +
      Number(this.paymentTermsControl.value) +
      Number(this.financialStabilityControl.value) +
      Number(this.commercialRiskAllocationControl.value) ;
  }
  

  prepareDataToSubmit() {
    // Mark controls as touched to trigger validation UI
    this.priceCompetitivenessControl.markAsTouched();
    this.paymentTermsControl.markAsTouched();
    this.financialStabilityControl.markAsTouched();
    this.commercialRiskAllocationControl.markAsTouched();

    // Check if all required fields have values
    if (
      this.priceCompetitivenessControl.value &&
      this.paymentTermsControl.value &&
      this.financialStabilityControl.value &&
      this.commercialRiskAllocationControl.value
    ) {
      const commercialEvaluationCriteria = {
        commercial_evaluation_criteria: [
          {
            name: 'Price Competitiveness',
            weight: this.priceCompetitivenessControl.value,
          },
          { name: 'Payment Terms', weight: this.paymentTermsControl.value },
          {
            name: 'Financial Stability',
            weight: this.financialStabilityControl.value,
          },
          {
            name: 'Commercial Risk Allocation',
            weight: this.commercialRiskAllocationControl.value,
          },
        ],
      };
      this.calculateTotal();
      if (this.totalWeight !== 100) {
        this.toast.danger(
          `Total weight must be 100, but got ${this.totalWeight}`,
          'ERROR',
          4000
        );
        console.error(`Total weight must be 100, but got ${this.totalWeight}`);
        return false;
      }

      return commercialEvaluationCriteria;
    }
  }

  moveToNextStep() {
    const dataToSubmit = this.prepareDataToSubmit();

    if (dataToSubmit) {
      this.TenderingService.storeCommercialEvaluationCriteria(
        dataToSubmit,
        this.projectId
      ).subscribe({
        next: (response: any) => {
          if (response?.meta?.success) {
            this.toast.success(
              'Commercial evaluation criteria weightage submitted successfully',
              'SUCCESS'
            );
            this.closeModalEvent.emit(this.closeModal);
            this.router.navigate(
              ['/initiate/rfp', this.projectId, response?.data?.id],
              {
                queryParams: {
                  projectName: this.projectName,
                  projectIdentifier: this.projectIdentifier,
                },
              }
            );
          } else {
            this.toast.danger(
              'Commercial evaluation criteria weightage could not be saved',
              'DANGER',
              4000
            );
          }
        },
        error: (error: any) => {
          console.error('Failed to submit:', error);
          this.toast.danger(error.error.message, 'DANGER', 4000);
        },
      });
    }
  }

  moveToPreviousStep() {
    this.IttWrapperComponent.previousStep();
  }
}
